import useQuestionnaire from 'hooks/useQuestionnaire';
import { QUESTIONNAIRE_CODE } from 'lib/constants';
import { checkSpanishLocale, cn, extractLabelAndComment } from 'lib/utils';
import { useEffect, useMemo, useState } from 'react';
import { useGetQuestionnaireOptionSearchV3 } from 'services/profile/hooks';
import { useQuestionnaireStore } from 'store/questionnaire';
import QuestionnaireContainer from './components/QuestionnaireContainer';
import { motion } from 'framer-motion';
import { Button } from 'components/ui/button';
import { Search, X } from 'lucide-react';
import MedicalConditionSearch from './components/MedicalConditionSearch';
import { SearchChoice } from 'services/profile/type';
import { useTranslation } from 'react-i18next';

export default function SpecifyMedicalCondition() {
  const { t } = useTranslation();
  const questionData = useQuestionnaireStore((store) => store.questionData);

  const isSpanish = checkSpanishLocale();

  const { data: searchOptions } = useGetQuestionnaireOptionSearchV3({
    code: questionData?.code || QUESTIONNAIRE_CODE.SPECIFY_MEDICAL_CONDITION,
    search_text: '',
  });

  const { onSubmit, isLoading } = useQuestionnaire();

  const [selectedConditions, setSelectedConditions] = useState<SearchChoice[]>(
    []
  );
  const [searchValues, setSearchValues] = useState<SearchChoice[]>([]);
  const [openSearch, setOpenSearch] = useState(false);

  const englishChoices = JSON.parse(questionData?.eng_choices || '[]');
  const spanishChoices = JSON.parse(questionData?.spanish_choices || '[]');

  const mostCommonConditions: { en: SearchChoice; es: SearchChoice }[] =
    useMemo(
      () =>
        englishChoices.map((choice: string, index: number) => ({
          en: { id: null, name: choice },
          es: { id: null, name: spanishChoices[index] },
        })),
      [englishChoices, spanishChoices]
    );
  const localeCommonConditions = useMemo(
    () =>
      mostCommonConditions.map(
        (condition) => condition?.[isSpanish ? 'es' : 'en']
      ),
    [mostCommonConditions, isSpanish]
  );

  useEffect(() => {
    setSelectedConditions(searchValues);
  }, [searchValues]);

  useEffect(() => {
    if (questionData?.user_eng_choices || questionData?.user_spanish_choices) {
      const userChoices =
        questionData?.[isSpanish ? 'user_spanish_choices' : 'user_eng_choices'];

      let parsedAnswer;
      try {
        parsedAnswer = JSON.parse(userChoices);
      } catch (error) {
        parsedAnswer = userChoices;
      }

      if (Array.isArray(parsedAnswer)) {
        if (!parsedAnswer?.length) {
          return setSelectedConditions([]);
        }

        let selected: SearchChoice[] = [];
        if (parsedAnswer[0]?.choice) {
          selected = parsedAnswer.map(
            (item: { id: string; choice: string }) => ({
              id: item.id,
              name: item.choice,
            })
          );
        } else {
          selected = parsedAnswer.map((item: string) => ({
            id: null,
            name: item,
          }));
        }
        setSelectedConditions(selected);
        return;
      }

      let answerArray: string[] = [];
      userChoices.split(',').forEach((item) => {
        const otherItem = item.split(':').map((i) => i.trim());
        if (otherItem.length > 1) {
          const splitOther = otherItem[1].split(',').map((i) => i.trim());
          answerArray = [...answerArray, ...splitOther];
        } else {
          answerArray.push(otherItem[0]);
        }
      });
      const filteredAnswer = answerArray.filter((item) => item);

      const answerWithId = filteredAnswer.map((ans) => ({
        id: null,
        name: ans,
      }));
      setSelectedConditions(answerWithId);
    }
  }, [isSpanish, questionData]);

  const onHandleSelection = (condition: SearchChoice) => {
    if (selectedConditions.map((c) => c.name).includes(condition.name)) {
      setSelectedConditions(
        selectedConditions.filter((c) => c.name !== condition.name)
      );
    } else {
      setSelectedConditions([...selectedConditions, condition]);
    }
  };

  const onClearAll = () => setSelectedConditions([]);

  const onContinue = () => {
    let englishConditions = null,
      spanishConditions = null;
    if (selectedConditions.length) {
      const formattedConditions = selectedConditions.map((cond) => ({
        choice: cond.name,
        id: cond.id,
      }));
      englishConditions = isSpanish ? null : formattedConditions;
      spanishConditions = isSpanish ? formattedConditions : null;
    }

    onSubmit({
      choice_value: englishConditions,
      spanish_choice_value: spanishConditions,
      questionData,
      code: questionData?.code,
    });
  };

  return (
    <>
      <QuestionnaireContainer
        questionData={questionData}
        isLoading={isLoading}
        onContinue={onContinue}
        questionNumber={10}
      >
        <button
          onClick={() => setOpenSearch(true)}
          className='flex items-center w-2/3 gap-2 px-4 text-sm text-gray-500 bg-gray-100 rounded-md h-9'
        >
          <Search className='size-4' />
          <span className='pt-0.5 font-medium'>{t('search_here')}</span>
        </button>
        <div className='mt-4'>
          <h1 className='mb-1 font-semibold'>{t('most_common')}</h1>
          <div className='flex flex-wrap gap-2'>
            {localeCommonConditions.map((condition) => {
              const isSelected = selectedConditions
                .map((cond) => cond.name)
                .includes(condition.name);
              const { label, comment } = extractLabelAndComment(condition.name);
              return (
                <motion.div
                  key={condition.name}
                  className={cn(
                    'bg-[#F3F4F6] text-sm font-medium text-[#656D76] px-4 rounded-xl py-2 min-h-8 flex items-center justify-center cursor-pointer transition-all duration-150',
                    isSelected && 'text-white bg-primary'
                  )}
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.15 }}
                  onClick={() => onHandleSelection(condition)}
                >
                  <p className='leading-5'>
                    {label} {comment}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>

        <div className='mt-4'>
          <div className='flex items-center justify-between'>
            <h1 className='font-semibold'>{t('selected')}</h1>
            <Button
              variant='link'
              className='text-sm underline transition-all duration-300 hover:no-underline text-rose-500 h-9'
              disabled={!selectedConditions.length}
              onClick={onClearAll}
            >
              {t('clear_all')}
            </Button>
          </div>
          <div className='flex flex-wrap gap-2'>
            {selectedConditions?.length ? (
              selectedConditions.map((condition) => {
                const { label, comment } = extractLabelAndComment(
                  condition.name
                );
                return (
                  <motion.div
                    key={condition.name}
                    className={cn(
                      'border border-gray-300 text-sm font-medium text-[#656D76] px-4 rounded-xl py-2 min-h-8 flex items-center justify-between cursor-pointer transition-all duration-150 gap-1.5'
                    )}
                    whileTap={{ scale: 0.9 }}
                    transition={{ duration: 0.15 }}
                    onClick={() => onHandleSelection(condition)}
                  >
                    <p className='leading-5'>
                      {label} {comment}
                    </p>
                    <X className='w-4 h-4' />
                  </motion.div>
                );
              })
            ) : (
              <div
                className={cn(
                  'bg-[#F3F4F6] text-sm font-medium text-[#656D76] px-4 rounded-xl h-8 flex items-center justify-center cursor-pointer transition-all duration-150'
                )}
              >
                <p className='leading-5'>{t('none')}</p>
              </div>
            )}
          </div>
        </div>
      </QuestionnaireContainer>

      <MedicalConditionSearch
        open={openSearch}
        onClose={() => setOpenSearch(false)}
        setSelected={setSearchValues}
        searchOptions={
          searchOptions || { eng_choices: [], spanish_choices: [] }
        }
      />
    </>
  );
}
